import { arrayBufferToBase64, base64ToArrayBuffer, concatNonceAndCiphertext, decryptAES, encryptAES, generateAESKey, splitNonceAndCiphertext } from './crypto';

function localKeyStorageKey(kind: 'dm' | 'group', id: string): string {
  return `atten:${kind}:key:${id}`;
}

async function importConversationKey(rawKeyBase64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', base64ToArrayBuffer(rawKeyBase64), { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}

export function getConversationKeyRaw(kind: 'dm' | 'group', id: string): string | null {
  return localStorage.getItem(localKeyStorageKey(kind, id));
}

export function setConversationKeyRaw(kind: 'dm' | 'group', id: string, rawKeyBase64: string): void {
  localStorage.setItem(localKeyStorageKey(kind, id), rawKeyBase64);
}

export async function ensureConversationKey(kind: 'dm' | 'group', id: string, rawKeyBase64: string): Promise<CryptoKey> {
  const existing = getConversationKeyRaw(kind, id);
  if (!existing) {
    setConversationKeyRaw(kind, id, rawKeyBase64);
    return importConversationKey(rawKeyBase64);
  }
  return importConversationKey(existing);
}

export async function getOrCreateConversationKey(kind: 'dm' | 'group', id: string): Promise<CryptoKey> {
  const storageKey = localKeyStorageKey(kind, id);
  const existing = localStorage.getItem(storageKey);
  if (existing) {
    return importConversationKey(existing);
  }

  const key = await generateAESKey();
  const raw = (await crypto.subtle.exportKey('raw', key)) as ArrayBuffer;
  const rawBase64 = arrayBufferToBase64(raw);
  localStorage.setItem(storageKey, rawBase64);
  return importConversationKey(rawBase64);
}

export async function encryptConversationText(text: string, key: CryptoKey): Promise<string> {
  const { ciphertext, nonce } = await encryptAES(key, new TextEncoder().encode(text).buffer as ArrayBuffer);
  return arrayBufferToBase64(concatNonceAndCiphertext(nonce, ciphertext));
}

export async function decryptConversationText(ciphertextBase64: string, key: CryptoKey): Promise<string> {
  const payload = base64ToArrayBuffer(ciphertextBase64);
  const { nonce, ciphertext } = splitNonceAndCiphertext(payload);
  const plaintext = await decryptAES(key, ciphertext, nonce);
  return new TextDecoder().decode(plaintext);
}
